'use client'

import { Lock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTextDirection, useTranslation } from '@/lib/i18n'
import { UserText } from '@/components/ui/user-text'
import { useVcroHeatmap } from '@/lib/vcro/org-api'
import { FACTOR_GROUP_KEY, HIDDEN_REASON_KEY } from '@/lib/vcro/labels'
import { TOTAL_BUCKET, heatCellBackground, heatCellTone, heatLegendBackground, heatScaleMax } from '@/lib/vcro/org'
import { formatPoints } from '@/lib/vcro/format'
import type { DimensionId, VcroHeatmap, VcroHiddenReason } from '@/lib/vcro/types'
import { VcroCard } from '../vcro-card'
import { CardEmpty, CardError, CardLoading } from '../card-states'

/**
 * The heatmap: a row per bucket of the chosen dimension (the organisation
 * first), a column per factor group, each cell the average measured person's
 * SIGNED POINTS from that group of the engine's factors (W2-API polish,
 * 2026-10-03: "+6.2", "−1.3", the unit the engine's explanation of a person
 * speaks; each point moves the 0..100 score by one; attitude is how far the
 * survey moves the score, measured for departments only). Colour is a
 * diverging scale centred on 0 and stretched to the grid's largest absolute
 * value, so the strongest cell is always the full pole; every cell prints its
 * figure, so colour is never the only signal.
 *
 * A bucket whose score is withheld is a DESIGNED row: its people count, a
 * hatched lock in every cell, no figure, and WHY (fewer than 5 people, too few
 * measured, or withheld to protect a smaller group). A cell with no data says
 * so. With no survey answers at all the attitude column is left out and one
 * note under the grid says so. A real table, so it reads right to left in
 * Arabic by itself; on a phone it scrolls inside its card with the row names
 * held in place.
 */

/** A hatch on the muted surface: "withheld on purpose", not "missing". */
const HATCH =
  'bg-[repeating-linear-gradient(135deg,var(--muted)_0_5px,color-mix(in_oklab,var(--muted)_40%,transparent)_5px_10px)]'

export function HeatmapCard({ dimension, dimensionLabel }: { dimension: DimensionId; dimensionLabel: string }) {
  const { t } = useTranslation()
  const { data, isLoading, isError, refetch, isFetching } = useVcroHeatmap(dimension)
  return (
    <VcroCard
      testId="vcro-heatmap-card"
      title={t('vcro.riskometer.heatmap.title')}
      description={t('vcro.riskometer.heatmap.description', { dimension: dimensionLabel })}
    >
      {isError && !data ? (
        <CardError onRetry={() => refetch()} />
      ) : isLoading || !data ? (
        <CardLoading className="h-[320px]" />
      ) : (
        <div className={cn('transition-opacity', isFetching && 'opacity-60')}>
          <HeatmapView heatmap={data} />
        </div>
      )}
    </VcroCard>
  )
}

export function HeatmapView({ heatmap }: { heatmap: VcroHeatmap }) {
  const { t, locale } = useTranslation()
  const dir = useTextDirection()
  const attitudeAt = heatmap.columns.indexOf('attitude')
  // Nobody answered a survey: the column would be a column of dots, so it is left out.
  const noAttitude = attitudeAt >= 0 && heatmap.attitudeResponses === 0
  const shownAt = heatmap.columns.map((_, i) => i).filter((i) => !(noAttitude && i === attitudeAt))
  const columns = heatmap.columns.map((c) => t(FACTOR_GROUP_KEY[c]))
  const departmentsOnly = !noAttitude && heatmap.dimension !== 'department' && attitudeAt >= 0
  const pick = (cells: (number | null)[]) => shownAt.map((i) => cells[i] ?? null)
  const withheldCount = heatmap.rows.filter((r) => r.hiddenReason !== null).length
  const bucketName = (label: string) => label || t('vcro.breakdown.notSet')
  const max = heatScaleMax([pick(heatmap.organisation), ...heatmap.rows.filter((r) => r.hiddenReason === null).map((r) => pick(r.cells))])
  const points = (v: number) => formatPoints(v, locale)

  if (heatmap.rows.length === 0 && heatmap.organisation.every((v) => v === null)) {
    return <CardEmpty title={t('vcro.riskometer.heatmap.emptyTitle')} body={t('vcro.riskometer.heatmap.emptyBody')} testId="vcro-heatmap-empty" />
  }

  // The cell that raises risk most, for the screen-reader summary (a figure the API sent, chosen, not computed).
  let top: { bucket: string; column: string; value: number } | null = null
  for (const row of heatmap.rows) {
    if (row.hiddenReason !== null) continue
    for (const i of shownAt) {
      const v = row.cells[i]
      if (v !== null && v !== undefined && (top === null || v > top.value)) top = { bucket: bucketName(row.label), column: columns[i], value: v }
    }
  }
  const topCell = top as { bucket: string; column: string; value: number } | null
  const summary = [
    !topCell
      ? t('vcro.riskometer.heatmap.summaryNone')
      : topCell.value > 0
        ? t('vcro.riskometer.heatmap.summary', { bucket: topCell.bucket, column: topCell.column, value: points(topCell.value) })
        : t('vcro.riskometer.heatmap.summaryNoRaise'),
    withheldCount > 0 ? t('vcro.riskometer.heatmap.summaryHidden', { count: withheldCount }) : '',
  ]
    .filter(Boolean)
    .join(' ')

  const cell = (rowName: string, value: number | null, i: number, reason: VcroHiddenReason | null) => {
    const column = columns[i]
    if (reason !== null) {
      return (
        <td
          key={i}
          data-state="hidden"
          aria-label={t('vcro.riskometer.heatmap.cellEmpty', { bucket: rowName, column, why: t(HIDDEN_REASON_KEY[reason].label) })}
          title={t(HIDDEN_REASON_KEY[reason].why)}
          className={cn('h-9 rounded-md p-0', HATCH)}
        >
          <span className="flex h-full items-center justify-center">
            <Lock className="size-3 text-muted-foreground/70" aria-hidden="true" />
          </span>
        </td>
      )
    }
    if (value === null) {
      const why = i === attitudeAt && departmentsOnly ? t('vcro.riskometer.heatmap.departmentsOnly') : t('vcro.riskometer.heatmap.noData')
      return (
        <td
          key={i}
          data-state="empty"
          aria-label={t('vcro.riskometer.heatmap.cellEmpty', { bucket: rowName, column, why })}
          title={why}
          className="h-9 rounded-md border border-dashed bg-transparent p-0 text-center text-xs text-muted-foreground"
        >
          <span aria-hidden="true">·</span>
        </td>
      )
    }
    return (
      <td
        key={i}
        data-state="value"
        data-tone={heatCellTone(value)}
        aria-label={t('vcro.riskometer.heatmap.cellValue', { bucket: rowName, column, value: points(value) })}
        className="h-9 rounded-md p-0 text-center text-xs font-semibold tabular-nums text-foreground"
        style={{ background: heatCellBackground(value, max) }}
      >
        <span aria-hidden="true" dir="ltr">
          {points(value)}
        </span>
      </td>
    )
  }

  const rowHeader = (name: string, people: number | null, reason: VcroHiddenReason | null, organisation = false) => (
    <th
      scope="row"
      className={cn(
        'sticky start-0 z-10 max-w-[11rem] bg-card py-1 pe-3 text-start align-middle font-normal',
        organisation && 'font-semibold',
      )}
    >
      <span className="block truncate text-xs sm:text-[13px]">
        {organisation ? name : <UserText>{name}</UserText>}
      </span>
      {people !== null ? (
        <span className="flex items-center gap-1 text-[11px] text-muted-foreground tabular-nums" title={reason ? t(HIDDEN_REASON_KEY[reason].why) : undefined}>
          {reason ? <Lock className="size-3" aria-hidden="true" /> : null}
          {t('vcro.riskometer.heatmap.people', { count: people })}
          {reason ? <span className="sr-only"> · {t(HIDDEN_REASON_KEY[reason].label)}</span> : null}
        </span>
      ) : null}
    </th>
  )

  return (
    <figure className="m-0 space-y-3">
      <div className="-mx-1 overflow-x-auto px-1 pb-1" tabIndex={0} aria-label={t('vcro.riskometer.heatmap.title')} role="region">
        <table data-testid="vcro-heatmap" dir={dir} className="w-full min-w-[720px] border-separate border-spacing-1 text-sm">
          <thead>
            <tr>
              <th scope="col" className="sticky start-0 z-10 bg-card">
                <span className="sr-only">{t('vcro.riskometer.heatmap.bucketColumn')}</span>
              </th>
              {shownAt.map((i) => (
                <th key={heatmap.columns[i]} scope="col" className="px-1 pb-1 align-bottom text-[11px] font-medium leading-tight text-muted-foreground">
                  {columns[i]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr data-testid="vcro-heatmap-row" data-bucket={TOTAL_BUCKET} className="[&>td]:ring-1 [&>td]:ring-foreground/25">
              {rowHeader(t('vcro.breakdown.total'), null, null, true)}
              {shownAt.map((i) => cell(t('vcro.breakdown.total'), heatmap.organisation[i] ?? null, i, null))}
            </tr>
            {heatmap.rows.map((row) => (
              <tr
                key={row.bucket}
                data-testid="vcro-heatmap-row"
                data-bucket={row.bucket}
                data-hidden={row.hidden ? 'true' : undefined}
                data-reason={row.hiddenReason ?? undefined}
              >
                {rowHeader(bucketName(row.label), row.people, row.hiddenReason)}
                {shownAt.map((i) => cell(bucketName(row.label), row.cells[i] ?? null, i, row.hiddenReason))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <figcaption className="sr-only" data-testid="vcro-heatmap-summary">
        {summary}
      </figcaption>
      <div className="flex flex-col gap-2 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
        {max > 0 ? (
          <div className="flex items-center gap-2" aria-hidden="true" data-testid="vcro-heat-legend-scale">
            <span className="tabular-nums" dir="ltr">
              {points(-max)}
            </span>
            <span className="h-2.5 w-36 rounded-full" data-testid="vcro-heat-legend" style={{ background: heatLegendBackground(dir) }} />
            <span className="tabular-nums" dir="ltr">
              {points(max)}
            </span>
          </div>
        ) : null}
        <p className="leading-snug" data-testid="vcro-heatmap-legend-text">
          {t('vcro.riskometer.heatmap.legend')}
        </p>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className={cn('inline-block size-3 rounded-sm', HATCH)} aria-hidden="true" />
          {t('vcro.riskometer.heatmap.hiddenLegend')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block size-3 rounded-sm border border-dashed" aria-hidden="true" />
          {t('vcro.riskometer.heatmap.noDataLegend')}
        </span>
      </div>
      {noAttitude ? (
        <p className="text-xs text-muted-foreground" data-testid="vcro-heatmap-no-attitude">
          {t('vcro.riskometer.heatmap.noAttitude')}
        </p>
      ) : departmentsOnly ? (
        <p className="text-xs text-muted-foreground" data-testid="vcro-heatmap-attitude-note">
          {t('vcro.riskometer.heatmap.attitudeNote')}
        </p>
      ) : null}
    </figure>
  )
}
